import { UserStub } from "api/common/types/auth.types";
import { TDateISO } from "api/common/types/datetime.types";
import { valueof } from "api/common/utils/type.utils";
import { BackendColor } from "utils/color";

export const MAILING_LIST_DOMAINS = {
  NIAP: "NIAP",
  CCP: "CCP",
  DEV: "DEV",
} as const;
export type MailingListDomains = valueof<typeof MAILING_LIST_DOMAINS>;

/**
 * One mailing-list domain option, served by GET /v2/mail/config/. The backend
 * owns which labels are available in the current environment and the email
 * domain each resolves to — the frontend no longer hardcodes these.
 */
export interface MailDomainOption {
  label: MailingListDomains;
  emailDomain: string;
}

/** Environment-specific mail configuration from GET /v2/mail/config/. */
export interface MailConfig {
  domains: MailDomainOption[];
}

// Exchange sync lifecycle — mirrors backend v2.mail.models.sync.SyncStatus
export const SYNC_STATUS = {
  PENDING: "PENDING",
  SYNCING: "SYNCING",
  SYNCED: "SYNCED",
  FAILED: "FAILED",
} as const;
export type SyncStatus = valueof<typeof SYNC_STATUS>;

// How Exchange represents a distribution-group member —
// mirrors backend v2.mail.models.mail_list_membership.RecipientType
export const RECIPIENT_TYPE = {
  USER: "user",
  MAIL_CONTACT: "mail_contact",
} as const;
export type RecipientType = valueof<typeof RECIPIENT_TYPE>;

// Query params for filtering/searching/pagination
export interface MailListQueryParams {
  category?: number;
  active?: boolean;
  domain?: MailingListDomains;
  search?: string;
  ordering?: string;
}

export interface MailListUsersQuery {
  listId: string;
  search?: string;
}

export interface MailUserListsQuery {
  userId: number;
  search?: string;
}

export interface MailListCategoriesQuery {
  search?: string;
  categoryId?: number;
}

export interface AddUsersToListPayload {
  mailListId: number;
  userIds: number[];
}

export interface RemoveUsersFromListPayload {
  mailListId: number;
  userIds: number[];
}

export interface AddUserToListsPayload {
  userId: number;
  listIds: number[];
}

// The shape of a single mail list item from the API
export interface MailList {
  id: number;
  name: string;
  description: string;
  domain: MailingListDomains;
  email: string;
  enabled: boolean;
  memberCount: number;
  category: MailCategory | null;
  createdOn: TDateISO;
  createdBy: UserStub;
  lastModified: TDateISO;
  editedBy: UserStub;
}

export interface MailListSummary {
  id: number;
  name: string;
  domain: string;
  email: string;
  category: MailCategory | null;
}

export interface MailAccount {
  id: number;
  name: string;
  email: string;
  associatedAccount: UserStub | null;
  mailingLists: MailListSummary[];
  mailingListCount: number;
  syncStatus: SyncStatus;
  lastSyncError: string;
  lastSyncedAt: TDateISO | null;
}

/**
 * A member of a mailing list — one row of the MailListMembership through table.
 *
 * The API returns a nested envelope ({ mailAccount, mailList, recipientType,
 * dateAdded, active }). The `listMailListUsers` endpoint flattens each row into
 * this shape so the DataGrid can read account fields at the top level and use
 * the account id as the row id (the bulk remove flow is account-id based).
 */
export interface MailListMember extends MailAccount {
  // membership metadata from the through table
  recipientType: RecipientType | null;
  dateAdded: TDateISO | null;
  /** Whether the membership row is active (not the account's own `active`). */
  membershipActive: boolean;
  // NOTE: the inherited syncStatus/lastSyncError/lastSyncedAt fields are
  // overwritten by the flatten step to carry the MEMBERSHIP's Exchange sync
  // state (is this account's membership in this list synced), not the
  // account's global sync state.
}

export interface MailCategory {
  id: number;
  name: string;
  description: string;
  color: BackendColor;
  /** Resolved MUI theme color path (e.g. "coral.light"). Set by transformResponse. */
  themeColor?: string;
}

export interface CreateMailListPayload {
  name: string;
  email: string;
  domain: MailingListDomains;
  description: string;
  categoryId: number;
}

export interface UpdateMailListPayload {
  id: number;
  name?: string;
  email?: string;
  domain?: MailingListDomains;
  description?: string;
  categoryId?: number;
  enabled?: boolean;
}

export interface CreateMailUserPayload {
  name: string;
  email: string;
  // Optional: the backend reads mail_list_ids with a default of [] (see
  // MailUserViewSet.perform_create), so a user can be created without being
  // assigned to any list (e.g. the inline "Create new user" in AddMember).
  mailListIds?: number[];
}

export interface UpdateMailUserPayload {
  id: number;
  name?: string;
  email?: string;
  mailListIds?: number[];
}

export interface CreateMailCategoryPayload {
  name: string;
  description: string;
  color?: string;
}

export interface UpdateMailCategoryPayload {
  id: string;
  name: string;
  description: string;
  color: string;
}

export interface DeleteBulkMailCategoryPayload {
  category_ids: number[];
}
